import Phaser from 'phaser';
import type { QuestView } from '@shared/types/quest';
import { attachCustomScrollbar, type CustomScrollbar } from '../../ui/customScrollbar';

/**
 * Ventana flotante (DOM) de las quests FIJADAS (pin), con look moderno/minimalista.
 * Por quest: estrella de favorito (destino de la ruta del World Map) + colapsable
 * de objetivos + título. Desplazable (scrollbar custom) pero de tamaño fijo.
 *
 * Vive sobre el canvas y queda **clampeada al rectángulo del canvas** (no al
 * viewport): la posición se calcula desde `game.canvas.getBoundingClientRect()`
 * y el drag/resize la mantienen dentro, para no dar ventaja a pantallas grandes.
 * Es hermana en espíritu de `WorldMapDock` (DOM fixed + CSS inyectado). El estado
 * (qué está pineado/favorito/colapsado + posición) lo posee `QuestUI`; este panel
 * sólo renderiza lo que recibe y dispara callbacks. Ver docs/QUESTS_PINNED_TRACKER.md.
 */

const STYLE_ID = 'qpp-style';
/** Ancho fijo del panel (px CSS). No redimensionable. */
const PANEL_W = 268;
/** Margen por defecto contra los bordes del canvas (px). */
const MARGIN = 12;

export interface QuestPinnedPanelCallbacks {
  /** Alternar la favorita (estrella). Single-favorite: setear reemplaza la anterior. */
  onToggleFavorite(questId: string): void;
  /** Alternar colapsado de la lista de objetivos de una quest. */
  onToggleCollapse(questId: string): void;
  /** Quitar la quest de las fijadas. */
  onUnpin(questId: string): void;
  /** El panel se arrastró; (x,y) es el offset relativo al canvas (px). */
  onDragEnd(x: number, y: number): void;
}

export interface QuestPinnedPanelData {
  quests: QuestView[];
  favoritedId: string | null;
  collapsedIds: Set<string>;
  /** Posición guardada (offset relativo al canvas) o null = esquina por defecto. */
  pos: { x: number; y: number } | null;
}

export class QuestPinnedPanel {
  private readonly scene: Phaser.Scene;
  private readonly cb: QuestPinnedPanelCallbacks;

  private root: HTMLDivElement | null = null;
  private headerEl: HTMLDivElement | null = null;
  private counterEl: HTMLSpanElement | null = null;
  private bodyEl: HTMLDivElement | null = null;
  private scrollbar: CustomScrollbar | null = null;

  private data: QuestPinnedPanelData = { quests: [], favoritedId: null, collapsedIds: new Set(), pos: null };
  /** Oculto explícitamente (p.ej. mientras el World Map está abierto). */
  private forceHidden = false;

  // Posición actual como offset relativo al canvas (px). null = usar default.
  private offX: number | null = null;
  private offY: number | null = null;

  // Drag.
  private dragging = false;
  private grabDX = 0;
  private grabDY = 0;

  private readonly onResize = (): void => this.reposition();
  private boundMove: ((e: PointerEvent) => void) | null = null;
  private boundUp: ((e: PointerEvent) => void) | null = null;

  constructor(scene: Phaser.Scene, cb: QuestPinnedPanelCallbacks) {
    this.scene = scene;
    this.cb = cb;
    this.scene.scale.on(Phaser.Scale.Events.RESIZE, this.onResize);
  }

  /** Actualiza los datos y re-renderiza. Crea el DOM la primera vez. */
  setData(data: QuestPinnedPanelData): void {
    this.data = data;
    if (data.pos) {
      this.offX = data.pos.x;
      this.offY = data.pos.y;
    }
    this.render();
  }

  /** Oculta/muestra el panel sin perder estado (p.ej. World Map abierto). */
  setForceHidden(hidden: boolean): void {
    if (this.forceHidden === hidden) return;
    this.forceHidden = hidden;
    this.applyVisibility();
    // Al reaparecer, reubicar (el canvas pudo cambiar de tamaño mientras estaba oculto,
    // y reposition() se saltea mientras display:none).
    if (!hidden) this.reposition();
  }

  destroy(): void {
    this.scene.scale.off(Phaser.Scale.Events.RESIZE, this.onResize);
    this.endDrag();
    this.scrollbar?.destroy();
    this.scrollbar = null;
    this.root?.remove();
    this.root = null;
    this.headerEl = null;
    this.counterEl = null;
    this.bodyEl = null;
  }

  // ── Construcción ───────────────────────────────────────────────────────────
  private ensureRoot(): void {
    if (this.root) return;
    ensureStyle();

    const root = document.createElement('div');
    root.className = 'qpp-root';
    // No filtrar input del panel al canvas (igual criterio que WorldMapDock).
    root.addEventListener('wheel', (e) => e.stopPropagation());
    root.addEventListener('keydown', (e) => e.stopPropagation());

    const header = document.createElement('div');
    header.className = 'qpp-header';
    const title = document.createElement('span');
    title.className = 'qpp-title';
    title.textContent = 'Misiones';
    const counter = document.createElement('span');
    counter.className = 'qpp-counter';
    header.appendChild(title);
    header.appendChild(counter);
    header.addEventListener('pointerdown', (e) => this.startDrag(e));
    root.appendChild(header);

    const body = document.createElement('div');
    body.className = 'qpp-body';
    root.appendChild(body);

    document.body.appendChild(root);
    this.root = root;
    this.headerEl = header;
    this.counterEl = counter;
    this.bodyEl = body;
    this.scrollbar = attachCustomScrollbar(body);
  }

  private render(): void {
    // Sin quests fijadas: no mostrar nada (ventana solo cuando hay pineadas).
    if (this.data.quests.length === 0) {
      this.applyVisibility();
      return;
    }
    this.ensureRoot();
    if (!this.root || !this.bodyEl || !this.counterEl) return;

    this.counterEl.textContent = `${this.data.quests.length}/3`;
    this.bodyEl.innerHTML = '';

    for (const q of this.data.quests) {
      this.bodyEl.appendChild(this.buildQuestEl(q));
    }

    this.applyVisibility();
    this.reposition();
    this.scrollbar?.refresh();
  }

  private buildQuestEl(q: QuestView): HTMLDivElement {
    const wrap = document.createElement('div');
    wrap.className = 'qpp-quest';

    const collapsed = this.data.collapsedIds.has(q.id);
    const isFav = this.data.favoritedId === q.id;

    const head = document.createElement('div');
    head.className = 'qpp-qhead';

    // Estrella de favorito (izquierda del colapsable).
    const star = document.createElement('button');
    star.className = 'qpp-star' + (isFav ? ' qpp-star--on' : '');
    star.textContent = isFav ? '★' : '☆';
    star.title = isFav ? 'Quitar de favorita (ruta del mapa)' : 'Marcar favorita (ruta en el mapa)';
    star.onclick = (e) => { e.stopPropagation(); this.cb.onToggleFavorite(q.id); };
    head.appendChild(star);

    // Chevron de colapso.
    const chevron = document.createElement('button');
    chevron.className = 'qpp-chevron';
    chevron.textContent = collapsed ? '▸' : '▾';
    chevron.onclick = (e) => { e.stopPropagation(); this.cb.onToggleCollapse(q.id); };
    head.appendChild(chevron);

    // Título (también alterna el colapso al click).
    const title = document.createElement('div');
    title.className = 'qpp-qtitle';
    title.textContent = q.nameEs;
    if (q.status === 'ready') {
      const tag = document.createElement('span');
      tag.className = 'qpp-ready';
      tag.textContent = '¡Listo!';
      title.appendChild(tag);
    }
    title.onclick = (e) => { e.stopPropagation(); this.cb.onToggleCollapse(q.id); };
    head.appendChild(title);

    // Botón quitar (unpin).
    const unpin = document.createElement('button');
    unpin.className = 'qpp-unpin';
    unpin.textContent = '✕';
    unpin.title = 'Dejar de fijar';
    unpin.onclick = (e) => { e.stopPropagation(); this.cb.onUnpin(q.id); };
    head.appendChild(unpin);

    wrap.appendChild(head);

    // Objetivos (colapsable con transición de max-height).
    const objs = document.createElement('div');
    objs.className = 'qpp-objs' + (collapsed ? '' : ' qpp-objs--open');
    for (const o of q.objectives) {
      const row = document.createElement('div');
      row.className = 'qpp-obj' + (o.complete ? ' qpp-obj--done' : '');
      const mark = document.createElement('span');
      mark.className = 'qpp-obj-mark';
      mark.textContent = o.complete ? '✔' : '•';
      const desc = document.createElement('span');
      desc.className = 'qpp-obj-desc';
      const count = o.target > 1 ? ` ${o.current}/${o.target}` : '';
      const locked = !o.active && !o.complete ? ' (bloqueado)' : '';
      desc.textContent = `${o.descEs}${count}${locked}`;
      row.appendChild(mark);
      row.appendChild(desc);
      objs.appendChild(row);
    }
    wrap.appendChild(objs);

    return wrap;
  }

  // ── Visibilidad + posición ─────────────────────────────────────────────────
  private applyVisibility(): void {
    if (!this.root) return;
    const visible = !this.forceHidden && this.data.quests.length > 0;
    this.root.style.display = visible ? 'flex' : 'none';
  }

  /** Rectángulo del canvas del juego en coords de viewport. */
  private canvasRect(): DOMRect | null {
    const canvas = this.scene.game.canvas;
    return canvas ? canvas.getBoundingClientRect() : null;
  }

  /** Reubica el panel dentro del canvas según el offset guardado (o el default). */
  private reposition(): void {
    if (!this.root || this.root.style.display === 'none') return;
    const rect = this.canvasRect();
    if (!rect) return;
    const w = this.root.offsetWidth || PANEL_W;
    const h = this.root.offsetHeight;

    // Ajustar alto máximo del body para que el panel entre dentro del canvas.
    if (this.bodyEl) {
      this.bodyEl.style.maxHeight = `${Math.max(120, Math.min(460, rect.height * 0.6))}px`;
    }

    // Default: esquina superior derecha del canvas.
    let ox = this.offX ?? (rect.width - w - MARGIN);
    let oy = this.offY ?? MARGIN;
    ox = clamp(ox, MARGIN, Math.max(MARGIN, rect.width - w - MARGIN));
    oy = clamp(oy, MARGIN, Math.max(MARGIN, rect.height - h - MARGIN));

    this.root.style.left = `${Math.round(rect.left + ox)}px`;
    this.root.style.top = `${Math.round(rect.top + oy)}px`;
  }

  // ── Drag (clampeado al canvas) ──────────────────────────────────────────────
  private startDrag(e: PointerEvent): void {
    if (!this.root) return;
    const rect = this.canvasRect();
    if (!rect) return;
    this.dragging = true;
    // Grab respecto del top-left actual del panel.
    const cur = this.root.getBoundingClientRect();
    this.grabDX = e.clientX - cur.left;
    this.grabDY = e.clientY - cur.top;
    this.boundMove = (ev) => this.onDragMove(ev);
    this.boundUp = () => this.endDrag(true);
    window.addEventListener('pointermove', this.boundMove);
    window.addEventListener('pointerup', this.boundUp);
    e.preventDefault();
  }

  private onDragMove(e: PointerEvent): void {
    if (!this.dragging || !this.root) return;
    const rect = this.canvasRect();
    if (!rect) return;
    const w = this.root.offsetWidth || PANEL_W;
    const h = this.root.offsetHeight;
    // Offset relativo al canvas, clampeado dentro de sus bordes.
    let ox = e.clientX - this.grabDX - rect.left;
    let oy = e.clientY - this.grabDY - rect.top;
    ox = clamp(ox, MARGIN, Math.max(MARGIN, rect.width - w - MARGIN));
    oy = clamp(oy, MARGIN, Math.max(MARGIN, rect.height - h - MARGIN));
    this.offX = ox;
    this.offY = oy;
    this.root.style.left = `${Math.round(rect.left + ox)}px`;
    this.root.style.top = `${Math.round(rect.top + oy)}px`;
  }

  private endDrag(report = false): void {
    if (this.boundMove) window.removeEventListener('pointermove', this.boundMove);
    if (this.boundUp) window.removeEventListener('pointerup', this.boundUp);
    this.boundMove = null;
    this.boundUp = null;
    if (report && this.dragging && this.offX !== null && this.offY !== null) {
      this.cb.onDragEnd(this.offX, this.offY);
    }
    this.dragging = false;
  }
}

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

// ── CSS (inyectado una vez) ──────────────────────────────────────────────────
function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
  .qpp-root{position:fixed;width:${PANEL_W}px;z-index:1600;display:flex;flex-direction:column;
    background:rgba(16,15,12,0.92);border:1px solid #8a7a4a;border-radius:10px;overflow:hidden;
    color:#e8e0cc;font-family:'Monogram',monospace;box-shadow:0 8px 24px rgba(0,0,0,0.55);
    backdrop-filter:blur(2px);}
  .qpp-header{display:flex;align-items:center;justify-content:space-between;gap:8px;
    padding:7px 12px;cursor:grab;user-select:none;border-bottom:1px solid #3a3222;
    background:linear-gradient(180deg,rgba(255,233,168,0.06),rgba(0,0,0,0));}
  .qpp-header:active{cursor:grabbing;}
  .qpp-title{font-size:15px;color:#ffe9a8;letter-spacing:1px;}
  .qpp-counter{font-size:12px;color:#8f866c;}
  .qpp-body{overflow-y:auto;padding:6px;display:flex;flex-direction:column;gap:4px;}
  .qpp-quest{border-radius:7px;background:rgba(0,0,0,0.18);}
  .qpp-qhead{display:flex;align-items:center;gap:4px;padding:5px 6px;}
  .qpp-star,.qpp-chevron,.qpp-unpin{flex:0 0 auto;background:none;border:none;cursor:pointer;
    font-family:inherit;padding:0;line-height:1;}
  .qpp-star{width:20px;font-size:16px;color:#6f6650;}
  .qpp-star:hover{color:#ffd56b;}
  .qpp-star--on{color:#ffd56b;text-shadow:0 0 6px rgba(255,213,107,0.6);}
  .qpp-chevron{width:16px;font-size:12px;color:#b3a98c;}
  .qpp-chevron:hover{color:#ffe9a8;}
  .qpp-qtitle{flex:1;min-width:0;font-size:14px;color:#e8e0cc;cursor:pointer;
    white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .qpp-qtitle:hover{color:#ffe9a8;}
  .qpp-ready{margin-left:6px;font-size:11px;color:#77dd77;}
  .qpp-unpin{width:18px;font-size:12px;color:#6f6650;}
  .qpp-unpin:hover{color:#dd6666;}
  .qpp-objs{max-height:0;overflow:hidden;transition:max-height 0.22s ease;
    padding:0 8px 0 26px;}
  .qpp-objs--open{max-height:600px;padding-bottom:6px;}
  .qpp-obj{display:flex;align-items:flex-start;gap:6px;font-size:12px;color:#c9c0a6;
    padding:2px 0;line-height:1.25;}
  .qpp-obj--done{color:#77dd77;}
  .qpp-obj-mark{flex:0 0 auto;width:10px;text-align:center;}
  .qpp-obj-desc{flex:1;min-width:0;}
  `;
  document.head.appendChild(style);
}
